import _formatErrorMessage from "@mui/x-internals/formatErrorMessage";
import _extends from "@babel/runtime/helpers/esm/extends";
import { warn } from '@base-ui/utils/warn';
import { warnOnce } from '@mui/x-internals/warning';
import { EMPTY_OBJECT } from '@base-ui/utils/empty';
import { DEFAULT_SCHEDULER_PREFERENCES, SchedulerStore } from "../internals/utils/SchedulerStore/index.mjs";
import { createChangeEventDetails } from "../base-ui-copy/utils/createBaseUIEventDetails.mjs";
export const DEFAULT_VIEWS = ['day', 'week', 'month', 'agenda'];
export const DEFAULT_VIEW = 'week';
export const DEFAULT_SHOULD_EVENT_REQUIRE_RESOURCE = false;
export const DEFAULT_EVENT_CALENDAR_PREFERENCES = _extends({}, DEFAULT_SCHEDULER_PREFERENCES, {
  showWeekends: true,
  showWeekNumber: false,
  showEmptyDaysInAgenda: true,
  isSidePanelOpen: true
});
export const DEFAULT_PREFERENCES_MENU_CONFIG = {
  toggleWeekendVisibility: true,
  toggleWeekNumberVisibility: true,
  toggleEmptyDaysInAgenda: true,
  toggleAmpm: true,
  toggleWeekStartsOn: false
};
const deriveStateFromParameters = parameters => {
  const views = parameters.views ?? DEFAULT_VIEWS;
  if (process.env.NODE_ENV !== 'production' && views.length === 0) {
    // TODO: fix mui/no-guarded-throw
    // eslint-disable-next-line mui/no-guarded-throw
    throw new Error(process.env.NODE_ENV !== "production" ? `MUI X Scheduler: EventCalendar received an empty \`views\` prop. ` + `This leaves the calendar without any view to render. ` + `Pass at least one view, or omit the prop to use the default set. ` + `See https://mui.com/x/react-scheduler/event-calendar/views/ for more details.` : _formatErrorMessage(261));
  }
  return {
    views
  };
};
function warnIfShouldEventRequireResourceMisconfigured(shouldEventRequireResource, resources) {
  if (shouldEventRequireResource && (resources == null || resources.length === 0)) {
    warnOnce(['MUI X Scheduler: `shouldEventRequireResource` is `true` but no resources are configured.', 'Users will not be able to select a resource, and events cannot be saved from the event dialog.', 'Either provide at least one resource, or set `shouldEventRequireResource={false}`.']);
  }
}
const mapper = {
  getInitialState: (schedulerInitialState, parameters) => {
    const shouldEventRequireResource = parameters.shouldEventRequireResource ?? DEFAULT_SHOULD_EVENT_REQUIRE_RESOURCE;
    warnIfShouldEventRequireResourceMisconfigured(shouldEventRequireResource, parameters.resources);
    return _extends({}, schedulerInitialState, deriveStateFromParameters(parameters), {
      preferences: parameters.preferences ?? parameters.defaultPreferences ?? EMPTY_OBJECT,
      preferencesMenuConfig: parameters.preferencesMenuConfig === false ? parameters.preferencesMenuConfig : _extends({}, DEFAULT_PREFERENCES_MENU_CONFIG, parameters.preferencesMenuConfig),
      viewConfig: null,
      view: parameters.view ?? parameters.defaultView ?? DEFAULT_VIEW,
      shouldEventRequireResource
    });
  },
  updateStateFromParameters: (newSchedulerState, parameters, updateModel) => {
    const shouldEventRequireResource = parameters.shouldEventRequireResource ?? DEFAULT_SHOULD_EVENT_REQUIRE_RESOURCE;
    warnIfShouldEventRequireResourceMisconfigured(shouldEventRequireResource, parameters.resources);
    const newState = _extends({}, newSchedulerState, deriveStateFromParameters(parameters), {
      shouldEventRequireResource
    });
    updateModel(newState, 'view', 'defaultView');
    updateModel(newState, 'preferences', 'defaultPreferences');
    return newState;
  }
};

/**
 * Base class that can be extended by premium stores.
 * Accepts instanceName as a parameter to allow subclasses to provide their own instance name.
 */
export class ExtendableEventCalendarStore extends SchedulerStore {
  constructor(parameters, adapter, instanceName, recurringEventsPlugin = null) {
    super(parameters, adapter, instanceName, mapper, recurringEventsPlugin);
    if (process.env.NODE_ENV !== 'production') {
      // Assert the initial state validity; `subscribe` only fires on subsequent state changes.
      this.assertViewValidity(this.state.view);
      this.subscribe(state => {
        this.assertViewValidity(state.view);
        return null;
      });
    }
  }
  assertViewValidity(view) {
    const views = this.state.views;
    if (!views.includes(view)) {
      throw new Error(process.env.NODE_ENV !== "production" ? `MUI X Scheduler: EventCalendar received the view "${view}", which is not part of the \`views\` prop (received: ${views.join(', ')}). ` + `This leaves the calendar in an inconsistent state where the current view is not one of the allowed options. ` + `Add "${view}" to the \`views\` prop, or pass a view that is already included. ` + `See https://mui.com/x/react-scheduler/event-calendar/views/ for more details.` : _formatErrorMessage(260, view, views.join(', '), view));
    }
  }
  setVisibleDateAndView = (visibleDate, view, event) => {
    const {
      visibleDate: visibleDateProp,
      view: viewProp,
      onVisibleDateChange,
      onViewChange
    } = this.parameters;
    const hasVisibleDateChange = visibleDate !== this.state.visibleDate;
    const hasViewChange = view !== this.state.view;
    if (!hasVisibleDateChange && !hasViewChange) {
      return;
    }
    this.assertViewValidity(view);
    const eventDetails = createChangeEventDetails('none', event.nativeEvent);
    if (hasVisibleDateChange) {
      onVisibleDateChange?.(visibleDate, eventDetails);
    }
    if (hasViewChange) {
      onViewChange?.(view, eventDetails);
    }
    if (eventDetails.isCanceled) {
      return;
    }
    const canSetVisibleDate = visibleDateProp === undefined && hasVisibleDateChange;
    const canSetView = viewProp === undefined && hasViewChange;
    if (canSetVisibleDate || canSetView) {
      this.update(_extends({}, canSetVisibleDate ? {
        visibleDate
      } : undefined, canSetView ? {
        view
      } : undefined));
    }
  };
  setSiblingVisibleDate = (delta, event) => {
    const siblingVisibleDateGetter = this.state.viewConfig?.siblingVisibleDateGetter;
    if (!siblingVisibleDateGetter) {
      warn('MUI X Scheduler: No config found for the current view. Please use useInitializeView in your custom view.');
      return;
    }
    this.setVisibleDate({
      visibleDate: siblingVisibleDateGetter({
        delta,
        state: this.state
      }),
      event
    });
  };

  /**
   * Sets the view of the calendar.
   */
  setView = (view, event) => {
    const {
      view: viewProp,
      onViewChange
    } = this.parameters;
    if (process.env.NODE_ENV !== 'production' && viewProp !== undefined && !onViewChange) {
      warn('MUI X Scheduler: EventCalendar is controlled (received a `view` prop) but `onViewChange` is not provided. View changes will be silently ignored.');
    }
    if (view !== this.state.view) {
      this.assertViewValidity(view);
      const eventDetails = createChangeEventDetails('none', event);
      onViewChange?.(view, eventDetails);
      if (!eventDetails.isCanceled && viewProp === undefined) {
        this.set('view', view);
      }
    }
  };

  /**
   * Goes to the previous visible date span based on the current view.
   */
  goToPreviousVisibleDate = event => this.setSiblingVisibleDate(-1, event);

  /**
   * Goes to the next visible date span based on the current view.
   */
  goToNextVisibleDate = event => this.setSiblingVisibleDate(1, event);

  /**
   * Goes to a specific day and set the view to 'day'.
   */
  switchToDay = (visibleDate, event) => {
    this.setVisibleDateAndView(visibleDate, 'day', event);
  };

  /**
   * Updates some preferences of the calendar.
   */
  setPreferences = (partialPreferences, event) => {
    const {
      preferences: preferencesProp,
      onPreferencesChange
    } = this.parameters;
    const updated = _extends({}, this.state.preferences, partialPreferences);
    const eventDetails = createChangeEventDetails('none', event);
    onPreferencesChange?.(updated, eventDetails);
    if (!eventDetails.isCanceled && preferencesProp === undefined) {
      this.set('preferences', updated);
    }
  };

  /**
   * Sets the method used to determine the previous / next visible date.
   * Returns the cleanup function.
   */
  setViewConfig = config => {
    this.set('viewConfig', config);
    return () => this.set('viewConfig', null);
  };
  buildPublicAPI() {
    return _extends({}, super.buildPublicAPI());
  }
}

/**
 * Store for the EventCalendar component.
 */
export class EventCalendarStore extends ExtendableEventCalendarStore {
  constructor(parameters, adapter) {
    super(parameters, adapter, 'EventCalendarStore');
  }
}